import {useMemo, useState, useEffect} from "react";
import PropTypes from "prop-types";
import {Link} from "react-router-dom";
import {useT} from "../i18n/i18n";

const CONSENT_KEY = "cookie_consent_v2";
const CONSENT_VERSION = 2;

const DEFAULT_PREFERENCES = {
    necessary: true,
    analytics: false,
    marketing: false,
};

function readStoredConsent() {
    try {
        const raw = window.localStorage.getItem(CONSENT_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        // If version changed, clear old consent
        if (data.version !== CONSENT_VERSION) return null;
        return data;
    } catch {
        return null;
    }
}

function saveConsent(preferences) {
    window.localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({
            version: CONSENT_VERSION,
            preferences,
            updatedAt: new Date().toISOString(),
        })
    );
    // Notify tracking scripts of consent change
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: preferences }));
}

export default function CookieBanner({language}) {
    const {t} = useT(language);
    const [consent, setConsent] = useState(() => readStoredConsent());
    const [isManageOpen, setIsManageOpen] = useState(false);
    const [draftPreferences, setDraftPreferences] = useState(() => {
        return consent?.preferences ?? DEFAULT_PREFERENCES;
    });
    const [showSuccess, setShowSuccess] = useState(false);

    const isVisible = useMemo(() => !consent, [consent]);

    useEffect(() => {
        if (showSuccess) {
            const timer = setTimeout(() => setShowSuccess(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [showSuccess]);

    if (!isVisible) {
        return null;
    }

    const acceptAll = () => {
        const preferences = {necessary: true, analytics: true, marketing: true};
        saveConsent(preferences);
        setConsent({preferences});
        setIsManageOpen(false);
        setShowSuccess(true);
    };

    const rejectAll = () => {
        const preferences = {necessary: true, analytics: false, marketing: false};
        saveConsent(preferences);
        setConsent({preferences});
        setIsManageOpen(false);
        setShowSuccess(true);
    };

    const savePreferences = () => {
        const preferences = {...draftPreferences, necessary: true};
        saveConsent(preferences);
        setConsent({preferences});
        setIsManageOpen(false);
        setShowSuccess(true);
    };

    const togglePreference = (key) => {
        if (key !== "necessary") {
            setDraftPreferences(prev => ({...prev, [key]: !prev[key]}));
        }
    };

    return (
        <section
            aria-label={t("cookies.title")}
            className="fixed inset-x-0 bottom-0 z-50 border-t border-white/20 bg-[#2A3D21] shadow-2xl"
            role="region"
        >
            <div className="mx-auto w-full max-w-6xl px-5 py-6 sm:px-8">
                {!isManageOpen ? (
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight text-white mb-3">
                            {t("cookies.title")}
                        </h2>
                        <p className="text-sm text-white/80 mb-4 leading-relaxed">
                            {t("cookies.description")}{" "}
                            <Link
                                to="/privacy-policy"
                                className="text-white hover:text-white/100 font-medium underline decoration-white/40 hover:decoration-white transition-all"
                            >
                                {t("cookies.privacyLink")}
                            </Link>
                            .
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                            <button
                                onClick={rejectAll}
                                className="px-5 py-2.5 rounded-lg border border-white/30 text-sm font-medium text-white hover:bg-white/10 hover:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#2A3D21] transition-all duration-200 backdrop-blur-sm"
                                aria-label="Reject all non-necessary cookies"
                            >
                                {t("cookies.reject")}
                            </button>
                            <button
                                onClick={() => setIsManageOpen(true)}
                                className="px-5 py-2.5 rounded-lg border border-white/30 text-sm font-medium text-white hover:bg-white/10 hover:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#2A3D21] transition-all duration-200 backdrop-blur-sm"
                                aria-label="Manage cookie preferences"
                            >
                                {t("cookies.manage")}
                            </button>
                            <button
                                onClick={acceptAll}
                                className="px-5 py-2.5 rounded-lg bg-white/20 text-white text-sm font-medium hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#2A3D21] border border-white/30 transition-all duration-200 backdrop-blur-sm"
                                aria-label="Accept all cookies"
                            >
                                {t("cookies.accept")}
                            </button>
                        </div>
                        {showSuccess && (
                            <p className="mt-3 text-xs text-white/70 flex items-center gap-2">
                                <span className="text-white">✓</span> {t("cookies.acceptedText")}
                            </p>
                        )}
                    </div>
                ) : (
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight text-white mb-4">
                            {t("cookies.details")}
                        </h2>
                        <div className="space-y-4 mb-6">
                            <div className="flex items-center gap-3 p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-white/15 transition-colors">
                                <input
                                    type="checkbox"
                                    id="necessary"
                                    checked={true}
                                    disabled
                                    className="w-4 h-4 cursor-not-allowed accent-white"
                                    aria-label="Necessary cookies are always enabled"
                                />
                                <div className="flex-1">
                                    <label htmlFor="necessary" className="font-medium text-sm text-white">
                                        {t("cookies.necessary")}
                                    </label>
                                    <p className="text-xs text-white/70">
                                        {t("privacy.necessaryText")}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-white/15 transition-colors cursor-pointer">
                                <input
                                    type="checkbox"
                                    id="analytics"
                                    checked={draftPreferences.analytics}
                                    onChange={() => togglePreference("analytics")}
                                    className="w-4 h-4 cursor-pointer accent-white"
                                    aria-label="Analytics cookies"
                                />
                                <div className="flex-1">
                                    <label htmlFor="analytics" className="font-medium text-sm text-white cursor-pointer">
                                        {t("cookies.analytics")}
                                    </label>
                                    <p className="text-xs text-white/70">
                                        {t("privacy.analyticsText")}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-white/15 transition-colors cursor-pointer">
                                <input
                                    type="checkbox"
                                    id="marketing"
                                    checked={draftPreferences.marketing}
                                    onChange={() => togglePreference("marketing")}
                                    className="w-4 h-4 cursor-pointer accent-white"
                                    aria-label="Marketing cookies"
                                />
                                <div className="flex-1">
                                    <label htmlFor="marketing" className="font-medium text-sm text-white cursor-pointer">
                                        {t("cookies.marketing")}
                                    </label>
                                    <p className="text-xs text-white/70">
                                        {t("privacy.marketingText")}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                            <button
                                onClick={() => setIsManageOpen(false)}
                                className="px-5 py-2.5 rounded-lg border border-white/30 text-sm font-medium text-white hover:bg-white/10 hover:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#2A3D21] transition-all duration-200 backdrop-blur-sm"
                            >
                                {t("cookies.close")}
                            </button>
                            <button
                                onClick={savePreferences}
                                className="px-5 py-2.5 rounded-lg bg-white/20 text-white text-sm font-medium hover:bg-white/30 focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#2A3D21] border border-white/30 transition-all duration-200 backdrop-blur-sm"
                            >
                                {t("cookies.save")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

CookieBanner.propTypes = {
    language: PropTypes.oneOf(["en", "it"]),
};
